import { Link } from "@inertiajs/react"
import { ClipboardList } from "lucide-react"
import { TableCard } from "@/components/blocks/TableCard"
import { Badge } from "@/components/catalyst/badge"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/catalyst/table"
import { useIntl } from "react-intl"
import { useFormat } from "@/lib/format"
import type { ExamSummary } from "@/types/exams"

/** Practice exams in a table card; the title link stretches over the row. */
export function ExamList({
  exams,
  hrefFor,
  admin = false,
}: {
  exams: ExamSummary[]
  hrefFor: (exam: ExamSummary) => string
  admin?: boolean
}) {
  const intl = useIntl()
  const format = useFormat()
  return (
    <TableCard>
      <Table bleed rowLinks className="[--gutter:--spacing(5)] [&_tbody_tr:last-child_td]:border-b-0">
        <TableHead>
          <TableRow>
            <TableHeader>{intl.formatMessage({ id: "exams.list_exam" })}</TableHeader>
            <TableHeader className="hidden md:table-cell">{intl.formatMessage({ id: "exams.list_cases" })}</TableHeader>
            <TableHeader className="hidden md:table-cell">{intl.formatMessage({ id: "exams.list_questions" })}</TableHeader>
            <TableHeader className="hidden sm:table-cell">{intl.formatMessage({ id: "exams.list_time" })}</TableHeader>
            <TableHeader className="text-right">{admin ? intl.formatMessage({ id: "exams.list_status" }) : intl.formatMessage({ id: "exams.list_best" })}</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {exams.map((exam) => (
            <TableRow key={exam.id}>
              <TableCell>
                <div className="flex items-center gap-3">
                  <ClipboardList className="size-4 shrink-0 text-ink-muted" />
                  <div className="min-w-0">
                    <Link href={hrefFor(exam)} className="font-medium text-ink-display no-underline after:absolute after:inset-0 hover:text-ink-display">
                      {exam.title}
                    </Link>
                    {admin && <div className="text-xs text-ink-muted">{exam.exam_version_name}</div>}
                  </div>
                </div>
              </TableCell>
              <TableCell className="hidden text-ink-muted md:table-cell">{format.caseCount(exam.case_count)}</TableCell>
              <TableCell className="hidden text-ink-muted md:table-cell">{format.questionCount(exam.question_count)}</TableCell>
              <TableCell className="hidden text-ink-muted sm:table-cell">{format.duration(exam.time_limit_minutes * 60)}</TableCell>
              <TableCell className="text-right">
                {admin ? (
                  <Badge color={exam.status === "published" ? "accent" : "zinc"}>
                    {exam.status === "published" ? intl.formatMessage({ id: "exams.published" }) : intl.formatMessage({ id: "exams.draft" })}
                  </Badge>
                ) : exam.unfinished ? (
                  <Badge color="amber">{intl.formatMessage({ id: "exams.in_progress" })}</Badge>
                ) : exam.best_score != null ? (
                  <span className="text-ink-muted">{format.number(exam.best_score / 100, { style: "percent" })}</span>
                ) : (
                  <span className="text-ink-muted">{intl.formatMessage({ id: "quizzes.not_taken" })}</span>
                )}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableCard>
  )
}
